/**
 * An app's Activity (FRONT-2943, ADR-0081): who changed what in the app,
 * newest first. A code change shows what it changed when asked, as the
 * Canvas Changes view does. The owner can undo a code change or the live
 * publish, and restore the version a change left the app at; an editor
 * reads the same list without those controls. Shown when the app's
 * `seesActivity` says so: the owner whenever the app keeps Git history, an
 * editor while `eddy.app-drafts` is on, the rule the server checks (FRONT-3368).
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { ArrowCounterClockwiseIcon, ClockCounterClockwiseIcon } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { useConfirm } from '~/components/confirm-dialog';
import { ChangedFilesList } from './changed-files';
import { formatRelativeTime } from '~/lib/relative-time';
import {
  useActivityChanges,
  useAppActivity,
  useRestoreVersion,
  useUndoChange,
  useUndoPublish,
  type ActivityItem,
} from '~/hooks/queries/use-app-activity';

export const OFFLINE_AGAIN = 'The app is offline again: that publish was the one that first took it live. Publish it to bring it back.';

/** Pure: what an undo or restore did, for its toast. `wentOffline` is the server's answer, never guessed here. */
export function undoneMessage(
  done: { kind: 'change' } | { kind: 'restore' } | { kind: 'publish'; change: { eventId: number } | null; wentOffline?: boolean },
): string {
  if (done.kind === 'change') return 'Undone. Publish the app to make it live.';
  if (done.kind === 'restore') return 'Restored. Publish the app to make it live.';
  const back = done.wentOffline ? OFFLINE_AGAIN : 'The app is back to the version before that publish.';
  return done.change ? `${back} The change itself is still in the app: undo it too from its entry.` : back;
}

/** Pure: the confirm before undoing a publish, warning when the server says it takes the app offline. */
export function undoPublishDescription(item: Pick<ActivityItem, 'undoTakesOffline'>): string {
  const rest = 'Its changes stay in the app until you undo them too.';
  return item.undoTakesOffline
    ? `This is the publish that first took the app live, so undoing it takes the app offline. ${rest}`
    : `The app goes back to the version that was live before it. ${rest}`;
}

/** Pure: why the row's controls cannot be pressed right now, or null. One decision for disabled and its explanation. */
export function activityActionGate(pending: boolean): string | null {
  return pending ? 'Working on it…' : null;
}

function ChangesView({ slug, eventId }: { slug: string; eventId: number }) {
  const { data, isLoading, isError, error } = useActivityChanges(slug, eventId, true);
  if (isError) return <p className="text-xs text-destructive">{(error as Error)?.message || "The changes can't be shown right now."}</p>;
  if (isLoading || !data) return <p className="text-xs text-muted-foreground">Loading the changes…</p>;
  return <ChangedFilesList files={data.files} />;
}

function ActivityRow({ item, slug }: { item: ActivityItem; slug: string }) {
  const [open, setOpen] = useState(false);
  const confirm = useConfirm();
  const undo = useUndoChange(slug);
  const undoPublish = useUndoPublish(slug);
  const restore = useRestoreVersion(slug);
  const blockedBy = activityActionGate(undo.isPending || undoPublish.isPending || restore.isPending);
  const fail = (e: Error) => toast.error(e.message || 'That could not be done');

  const onUndo = async () => {
    if (item.undo === 'publish' && item.publishId !== null) {
      const ok = await confirm({
        title: 'Undo this publish?',
        description: undoPublishDescription(item),
        confirmText: 'Undo publish',
      });
      if (ok) undoPublish.mutate(item.publishId, { onSuccess: (a) => toast.success(undoneMessage({ kind: 'publish', change: a.change, wentOffline: a.wentOffline })), onError: fail });
      return;
    }
    const ok = await confirm({
      title: 'Undo this change?',
      description: 'A new change reverses it. Nothing else is lost, and the app is not published until you publish it.',
      confirmText: 'Undo',
    });
    if (ok) undo.mutate(item.id, { onSuccess: () => toast.success(undoneMessage({ kind: 'change' })), onError: fail });
  };

  const onRestore = async () => {
    if (!item.restore) return;
    const ok = await confirm({
      title: 'Restore this version?',
      description: 'The app is changed back to how it was right after this. Everything since stays in its history.',
      confirmText: 'Restore',
    });
    if (ok) restore.mutate(item.restore, { onSuccess: () => toast.success(undoneMessage({ kind: 'restore' })), onError: fail });
  };

  return (
    <li className="space-y-1 rounded-md border border-border p-2.5" data-testid="activity-row">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 space-y-0.5">
          <p className="text-sm">{item.label}</p>
          <p className="text-xs text-muted-foreground">{formatRelativeTime(item.at) ?? ''}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {item.undo && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={blockedBy !== null}
              tooltip={blockedBy ?? (item.undo === 'publish' ? 'Put back the version that was live before this' : 'Reverse this change with a new one')}
              className="gap-1.5"
              onClick={() => void onUndo()}
            >
              <ArrowCounterClockwiseIcon size={14} />
              {item.undo === 'publish' ? 'Undo publish' : 'Undo'}
            </Button>
          )}
          {item.restore && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={blockedBy !== null}
              className="gap-1.5"
              tooltip={blockedBy ?? 'Change the app back to how it was right after this'}
              onClick={() => void onRestore()}
            >
              <ClockCounterClockwiseIcon size={14} />
              Restore this version
            </Button>
          )}
        </div>
      </div>
      {item.hasChanges && (
        <details className="text-xs" onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
          <summary className="cursor-pointer text-muted-foreground">Show the changes</summary>
          {open && <ChangesView slug={slug} eventId={item.id} />}
        </details>
      )}
    </li>
  );
}

export function ActivityPanel({ slug, isOwner }: { slug: string; isOwner: boolean }) {
  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } = useAppActivity(slug);
  const items = data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        {isOwner
          ? 'Everything that changed in this app, newest first. You can undo a change or a publish, or go back to an earlier version.'
          : 'Everything that changed in this app, newest first.'}
      </p>
      {isError ? (
        <p className="text-xs text-destructive">Could not load this app&apos;s activity.</p>
      ) : isLoading ? (
        <p className="text-xs text-muted-foreground">Loading activity…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nothing has happened in this app yet.</p>
      ) : (
        <>
          <ul className="space-y-2">
            {items.map((item) => (
              <ActivityRow key={item.id} item={item} slug={slug} />
            ))}
          </ul>
          {hasNextPage && (
            <Button type="button" variant="ghost" size="sm" disabled={isFetchingNextPage} onClick={() => void fetchNextPage()}>
              {isFetchingNextPage ? 'Loading…' : 'Show older activity'}
            </Button>
          )}
        </>
      )}
    </div>
  );
}
